import useAddressSuggestions from '../hooks/useAddressSuggestions'
import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  useMediaQuery,
  ButtonGroup,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Autocomplete,
  FormHelperText,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { config } from '../config';

import { NumericFormat } from 'react-number-format';
import { BsMagic } from 'react-icons/bs';
import objectStore from '../stores/objectStore';
import logger from '../utils/logger';
import apiClient from '../utils/apiClient';
import i18nStore from '../stores/i18nStore';

/* import FileUpload from './FileUpload' */

function DynamicFields({ fields, setFieldsFromDynamicFilelds, saleType, jsonFileNameInput }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // const [address, setAddress] = useState('') // Стейт для адреса

  const [address, setAddress] = useState('');
  const addressLookup = useAddressSuggestions();
  const { suggestions: addressSuggestions, loading: isLoadingAddressSuggestions, open: openAddressSuggestions, setOpen: setOpenAddressSuggestions, error: addressError } = addressLookup;
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isAddressSelectedFlag, setIsAddressSelectedFlag] = useState(false);
  const addressInputRef = useRef(null);

  // Стейт для значений всех полей
  const [fieldValues, setFieldValues] = useState({});
  // Стейт для ошибок валидации
  const [errors, setErrors] = useState({});
  // Флаг, показывающий, была ли выполнена валидация
  const [wasValidated, setWasValidated] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [hasGeneratedDescription, setHasGeneratedDescription] = useState(false);
  const [showAiWarning, setShowAiWarning] = useState(false);

  const handleAddressChange = (value) => {
    setAddress(value);
    setIsAddressSelectedFlag(false);
    setSelectedAddress(null);
    addressLookup.search(value);
    setFieldsFromDynamicFilelds((prev) => ({ ...prev, address: value, addressData: null }));
  };

  const handleSuggestionClick = (suggestion) => {
    const newValue = suggestion.value;
    const newData = suggestion.data;
    setAddress(newValue);
    setSelectedAddress(newData);
    setIsAddressSelectedFlag(true);
    addressLookup.clear();

    setFieldsFromDynamicFilelds((prev) => ({
      ...prev,
      address: newValue,
      addressData: newData,
    }));
  };
  // Обработчик для текстовых полей, текстовых областей и select
  const handleChange = async (key, value, controlType) => {
    /* 	logger.log(`key: ${key}`)
		logger.log(`value: ${value}`)
		logger.log(`controlType: ${controlType}`) */

    if (controlType === 'number' || controlType === 'float') {
      const parsedValue = parseFloat(value); // Преобразуем в число
      if (isNaN(parsedValue)) {
        logger.warn(`Invalid number for ${key}: ${value}`);
        return; // Не обновляем, если ввели не число
      }

      // Обрабатываем в зависимости от типа
      if (controlType === 'number') {
        value = Math.floor(parsedValue); // Округляем до целого для number
      } else if (controlType === 'float') {
        value = parsedValue; // Оставляем как есть для float
      }
    }

    if (key === 'address') handleAddressChange(value);

    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    setFieldsFromDynamicFilelds((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const shouldDisplayField = (field) => {
    const { dependencies, hideWhen } = field;

    // 1. Check dependencies (SHOW logic - OR for array format, AND for object format)
    let showBasedOnDependencies = true;
    if (dependencies) {
      const checkCondition = (depField, depValues) => {
        const actualValue = fieldValues[depField];
        if (Array.isArray(actualValue)) {
          // multiple = true
          return actualValue.some((val) => depValues.includes(val));
        }
        // multiple = false (single value)
        return depValues.includes(actualValue);
      };

      if (Array.isArray(dependencies)) {
        // Old array format (OR logic between conditions)
        showBasedOnDependencies = dependencies.some((condition) =>
          Object.entries(condition).every(([depField, depValues]) =>
            checkCondition(depField, depValues),
          ),
        );
      } else {
        // New object format (AND logic between conditions)
        showBasedOnDependencies = Object.entries(dependencies).every(([depField, depValues]) =>
          checkCondition(depField, depValues),
        );
      }
    }

    // If dependencies condition says hide, then hide.
    if (!showBasedOnDependencies) {
      return false;
    }

    // 2. Check hideWhen (HIDE if ALL conditions met - AND logic)
    if (hideWhen) {
      const allHideConditionsMet = Object.entries(hideWhen).every(([depField, depValues]) => {
        const actualValue = fieldValues[depField];
        // Check if the actual value matches *any* of the values specified for hiding in depValues
        if (Array.isArray(actualValue)) {
          // multiple = true
          // If *any* selected value is in the hide list for this field, this condition is true
          return actualValue.some((val) => depValues.includes(val));
        }
        // multiple = false (single value)
        return depValues.includes(actualValue); // If the single value is in the hide list, this condition is true
      });

      // If all hide conditions were met, hide the field.
      if (allHideConditionsMet) {
        return false;
      }
    }

    // If we reached here, dependencies allow showing and hideWhen conditions were not all met.
    return true;
  };

  // Группируем поля по group
  const groupedFields = Object.entries(fields).reduce((acc, [key, field]) => {
    const group = field.group || key; // Если group не задан, используем ключ как отдельную группу
    if (!acc[group]) acc[group] = [];
    acc[group].push({ key, ...field });
    return acc;
  }, {});

  // Обработчик для переключения значений кнопок
  const handleToggle = (key, optionId, multiple) => {
    setFieldValues((prev) => {
      if (multiple) {
        // Предыдущее значение (массив ID)
        const selectedIds = prev[key] || [];
        // Если в массиве уже есть optionId, убираем его, иначе добавляем
        const newValue = selectedIds.includes(optionId)
          ? selectedIds.filter((id) => id !== optionId)
          : [...selectedIds, optionId];

        return {
          ...prev,
          [key]: newValue,
        };
      } else {
        // Одиночное значение
        return {
          ...prev,
          [key]: optionId,
        };
      }
    });

    // Если нужно, аналогично меняем setFieldsFromDynamicFilelds(...)
    setFieldsFromDynamicFilelds((prev) => {
      if (multiple) {
        const selectedIds = prev[key] || [];
        const newValue = selectedIds.includes(optionId)
          ? selectedIds.filter((id) => id !== optionId)
          : [...selectedIds, optionId];
        return {
          ...prev,
          [key]: newValue,
        };
      } else {
        return {
          ...prev,
          [key]: optionId,
        };
      }
    });
  };

  const getAIDescription = async () => {
    // Предотвращаем повторную генерацию
    if (hasGeneratedDescription) return;

    // Устанавливаем флаг генерации
    setIsGeneratingDescription(true);

    // Get the jsonFileName from props (assuming it's passed to the component)
    const jsonFileName = jsonFileNameInput;

    // Find the corresponding JSON file data from the MobX store
    const jsonFileData = objectStore.jsonfiles.find((item) => item.type.fileName === jsonFileName);

    if (!jsonFileData) {
      logger.error(`JSON file not found: ${jsonFileName}`);
      setIsGeneratingDescription(false);
      return;
    }

    // Start building the prompt text for AI
    let promptText = '';

    // Add address if available in the component
    if (address) {
      promptText += `Адрес: ${address}\n`;
    }

    // Process each field in fieldValues
    for (const [fieldKey, fieldValue] of Object.entries(fieldValues)) {
      // Skip fields that should not be included in description
      if (
        ['our_commission', 'commentforme', 'commentforcolleagues', 'commentforall', 'photo'].includes(
          fieldKey,
        )
      ) {
        continue;
      }

      // Skip fields with value 0
      if (fieldValue === 0) {
        continue;
      }

      // Find the field definition in the JSON file
      const fieldDef = jsonFileData[fieldKey];
      if (!fieldDef) {
        continue; // Skip fields not found in the JSON definition
      }

      // Process field based on its type and value
      if (fieldDef.controlType === 'buttons' || fieldDef.controlType === 'dropdown') {
        if (Array.isArray(fieldValue) && fieldDef.multiple) {
          // Handle multiple selection fields
          const selectedOptions = fieldValue
            .map((id) => {
              const option = fieldDef.options.find((opt) => opt.id === id);
              return option ? option.value : null;
            })
            .filter((val) => val !== null);

          if (selectedOptions.length > 0) {
            promptText += `${fieldDef.label}: ${selectedOptions.join(', ')}\n`;
          }
        } else {
          // Handle single selection fields
          const option = fieldDef.options.find((opt) => opt.id === fieldValue);
          if (option) {
            promptText += `${fieldDef.label}: ${option.value}\n`;
          }
        }
      } else if (fieldDef.controlType === 'number' || fieldDef.controlType === 'float') {
        // Handle numeric fields
        promptText += `${fieldDef.label}: ${fieldValue}\n`;
      } else if (fieldDef.controlType === 'increment') {
        // Handle increment fields (including boolean fields)
        // Simply include the value without converting to Да/Нет
        promptText += `${fieldDef.label}: ${fieldValue}\n`;
      } else if (fieldDef.controlType === 'text' || fieldDef.controlType === 'textarea') {
        // Handle text fields (if needed)
        if (fieldValue && typeof fieldValue === 'string' && fieldValue.trim() !== '') {
          promptText += `${fieldDef.label}: ${fieldValue}\n`;
        }
      }
    }

    try {
      const response = await apiClient.post('/descriptionAi', { prompt: promptText });

      const data = response.data;
      logger.log('AI Prompt Text:', data.description);
      // Правильное обновление состояния через функцию setFieldValues
      setFieldValues((prev) => ({
        ...prev,
        description: data.description,
      }));

      // Также обновляем родительский компонент, если нужно
      setFieldsFromDynamicFilelds((prev) => ({
        ...prev,
        description: data.description,
      }));

      // Установка флагов после успешной генерации
      setHasGeneratedDescription(true);
      setShowAiWarning(true);
      setIsGeneratingDescription(false);

      return data.description; // Предполагаем, что сервер вернет объект с полем description
    } catch (error) {
      logger.error('Error fetching AI description:', error);
      setIsGeneratingDescription(false);
      throw error;
    }
  };

  function getMe() {
    logger.log(`fieldValues:`, JSON.stringify(fieldValues));
    logger.log(`fields:`, JSON.stringify(fields));
    logger.log(jsonFileNameInput);
  }

  // Функция для проверки всех обязательных полей
  const validateRequiredFields = () => {
    const newErrors = {};
    let isValid = true;

    // Проходим по всем полям
    Object.entries(fields)
      .filter(([key, field]) => shouldDisplayField(field)) // Только видимые поля
      .forEach(([key, field]) => {
        // Проверяем только поля с required: true
        if (field.required) {
          let fieldValid = true;

          // Проверка для разных типов полей
          if (field.controlType === 'buttons' || field.controlType === 'dropdown') {
            if (field.multiple) {
              // Если поле множественного выбора, проверяем что массив не пустой
              fieldValid = Array.isArray(fieldValues[key]) && fieldValues[key].length > 0;
            } else {
              // Если поле одиночного выбора, проверяем что значение существует
              fieldValid = fieldValues[key] !== undefined && fieldValues[key] !== null;
            }
          } else if (field.controlType === 'text' || field.controlType === 'textarea') {
            // Для текстовых полей проверяем наличие и не пустоту строки
            fieldValid =
              fieldValues[key] !== undefined &&
              fieldValues[key] !== null &&
              String(fieldValues[key]).trim() !== '';
          } else if (field.controlType === 'number' || field.controlType === 'float') {
            // Для числовых полей проверяем наличие значения
            fieldValid =
              fieldValues[key] !== undefined &&
              fieldValues[key] !== null &&
              String(fieldValues[key]).trim() !== '';
          } else if (field.controlType === 'increment') {
            // Для increment проверяем, что значение больше 0
            fieldValid =
              fieldValues[key] !== undefined && fieldValues[key] !== null && fieldValues[key] > 0;
          }

          // Если поле не прошло валидацию
          if (!fieldValid) {
            newErrors[key] = 'Это поле обязательно для заполнения';
            isValid = false;
          }
        }
      });

    // Обновляем состояние ошибок
    setErrors(newErrors);
    setWasValidated(true);

    return isValid;
  };

  // Экспортируем функцию валидации для родительского компонента
  useEffect(() => {
    // Добавляем функцию валидации в объект, переданный родительскому компоненту
    setFieldsFromDynamicFilelds((prev) => ({
      ...prev,
      validateDynamicFields: validateRequiredFields,
    }));
  }, [fieldValues]); // Зависимость от fieldValues, чтобы функция обновлялась при изменении полей

  return (
    <div>
       {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && <div onClick={() => logger.log('DynamicFields Render')}>% DynamicFields %</div>}

      <Box sx={{ marginBottom: 2, position: 'relative' }}>
        <Autocomplete
          freeSolo
          filterOptions={(options) => options}
          open={openAddressSuggestions}
          onOpen={() => {
            if (addressSuggestions.length > 0) {
              setOpenAddressSuggestions(true);
            }
          }}
          onClose={() => setOpenAddressSuggestions(false)}
          options={addressSuggestions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.value)}
          value={address}
          loading={isLoadingAddressSuggestions}
          loadingText="Загрузка..."
          noOptionsText={address.length > 3 ? "Нет подходящих адресов" : "Введите больше символов"}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue !== 'string') {
              handleSuggestionClick(newValue);
            } else if (newValue === null) {
              handleAddressChange('');
            } else if (typeof newValue === 'string') {
              setAddress(newValue);
              setIsAddressSelectedFlag(false);
              setOpenAddressSuggestions(false);
              setFieldsFromDynamicFilelds((prev) => ({
                ...prev,
                address: newValue,
                addressData: null,
              }));
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              handleAddressChange(newInputValue);
            } else if (reason === 'clear') {
              handleAddressChange('');
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              ref={addressInputRef}
              label="Адрес"
              size="small"
              fullWidth
              sx={{ maxWidth: '500px' }}
              placeholder={`${i18nStore.currentCountry}  Выберите адрес из подсказок`}
              error={Boolean(addressError) || (address.length > 0 && !isAddressSelectedFlag)}
              helperText={addressError || (address.length > 0 && !isAddressSelectedFlag ? 'Пожалуйста, выберите адрес из подсказок' : errors['address'] || '')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingAddressSuggestions ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      {Object.entries(fields)
        .filter(([key, field]) => shouldDisplayField(field)) // Используем новую функцию
        .map(([key, field]) => {
          switch (field.controlType) {
            case 'text':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="text" // Просто number, без step
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value, field.controlType)}
                      placeholder={field.placeholder || ''}
                      inputProps={{ maxLength: field.maxLength || 1024 }}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'number':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <NumericFormat
                      customInput={TextField}
                      label={field.label}
                      size="small"
                      required={field.required}
                      fullWidth={isSmallScreen}
                      thousandSeparator=" "
                      decimalScale={0} // Запрещает десятичные дроби
                      allowNegative={false} // Опционально: запрещает отрицательные числа
                      allowedDecimalSeparators={[]} // Пустой массив - запрещает все десятичные разделители
                      isAllowed={(values) => {
                        const { value } = values;
                        // Дополнительная проверка - убедиться, что значение целое
                        return /^\d+$/.test(value) || value === '';
                      }}
                      value={fieldValues[key] || ''}
                      onValueChange={(values) => {
                        // values.value содержит значение без форматирования
                        handleChange(key, values.value, field.controlType);
                      }}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      placeholder={field.placeholder || ''}
                      inputProps={{ maxLength: field.maxLength || 22 }}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'float':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <NumericFormat
                      customInput={TextField}
                      label={field.label}
                      size="small"
                      required={field.required}
                      fullWidth={isSmallScreen}
                      thousandSeparator=" "
                      allowedDecimalSeparators={['.']} // Разрешает и точку, и запятую
                      value={fieldValues[key] || ''}
                      onValueChange={(values) => {
                        handleChange(key, values.value, field.controlType);
                      }}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      placeholder={field.placeholder || ''}
                      inputProps={{ maxLength: field.maxLength || 22 }}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'empty_divider':
              return <div className="mt-12"></div>;
            case 'header':
              return (
                <div className="mt-10 mb-4">
                  <p className="text-xl font-semibold ">{field.title}</p>
                </div>
              );
            case 'header2':
              return (
                <div className="mt-7">
                  <p className="text-base font-normal">{field.title}</p>
                </div>
              );
            case 'increment':
              return (
                <Box
                  key={key}
                  sx={{
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                  <span className="border p-1 border-gray-400 rounded">
                    <label className="ml-2">{field.label}</label>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const newValue = Math.max(0, (fieldValues[key] || 0) - 1);
                        handleChange(key, newValue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        lineHeight: '1', // Выровнять высоту текста
                        padding: '0 6px', // Добавить симметричные отступы
                        minWidth: '40px', // Зафиксировать минимальную ширину для стабильности
                        marginBottom: '4px',
                        marginLeft: '20px',
                      }}>
                      -
                    </Button>
                    <span>{fieldValues[key] || 0}</span>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const newValue = (fieldValues[key] || 0) + 1;
                        handleChange(key, newValue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        lineHeight: '1', // Выровнять высоту текста
                        padding: '0 6px', // Добавить симметричные отступы
                        minWidth: '40px', // Зафиксировать минимальную ширину для стабильности
                      }}>
                      +
                    </Button>
                  </span>
                  {field.required && (
                    <div className="text-orange-500 font-extrabold text-xl">*</div>
                  )}
                </Box>
              );

            case 'textarea':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <TextField
                    label={field.label}
                    required={field.required}
                    multiline
                    rows={field.rows || 4}
                    value={
                      isGeneratingDescription && key === 'description'
                        ? 'Генерация описания...'
                        : fieldValues[key] || ''
                    }
                    onChange={(e) => handleChange(key, e.target.value)}
                    placeholder={field.placeholder || ''}
                    fullWidth={isSmallScreen} // Полная ширина только на мобильных
                    sx={{
                      maxWidth: isSmallScreen ? '100%' : '500px', // Ограничение ширины для больших экранов
                      width: '100%',
                      alignSelf: isSmallScreen ? 'stretch' : 'flex-start', // Прижатие влево на больших экранах
                    }}
                    inputProps={{ maxLength: field.maxLength || 3000 }}
                    error={!!errors[key]}
                    helperText={errors[key]}
                    InputProps={{
                      endAdornment:
                        isGeneratingDescription && key === 'description' ? (
                          <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                        ) : null,
                    }}
                  />
                  <div>
                    {key === 'description' && (
                      <>
                        {showAiWarning && (
                          <Alert
                            severity="warning"
                            sx={{
                              mt: 1,
                              mb: 1,
                              maxWidth: isSmallScreen ? '100%' : '500px',
                            }}>
                            Внимательно проверьте текст объявления, сгенерированный искусственным
                            интеллектом.
                          </Alert>
                        )}
                        <Button
                          variant="outlined"
                          color="success"
                          sx={{
                            mt: 1,
                            textTransform: 'none',
                          }}
                          onClick={() => getAIDescription()}
                          disabled={isGeneratingDescription || hasGeneratedDescription}>
                          {isGeneratingDescription ? (
                            <>
                              Генерация...
                              <CircularProgress color="inherit" size={16} sx={{ ml: 1 }} />
                            </>
                          ) : hasGeneratedDescription ? (
                            'Описание сгенерировано'
                          ) : (
                            <>
                              Сгенерировать с помощью ИИ <BsMagic className="ml-3" />
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                  {field.required && (
                    <div className="text-orange-500 font-extrabold text-xl">*</div>
                  )}
                </Box>
              );
            case 'dropdown':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <TextField
                    select
                    label={field.label}
                    size="small"
                    required={field.required}
                    fullWidth={isSmallScreen}
                    sx={{
                      maxWidth: isSmallScreen ? '100%' : '500px',
                      width: '100%',
                      alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                    }}
                    SelectProps={{
                      multiple: field.multiple,
                    }}
                    value={fieldValues[key] ?? (field.multiple ? [] : '')}
                    onChange={(e) => {
                      // Если multiple=true, e.target.value будет массивом [id1, id2, ...]
                      // Если multiple=false, e.target.value будет одиночным id
                      handleChange(key, e.target.value);
                    }}
                    error={!!errors[key]}
                    helperText={errors[key]}>
                    {field.options.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              );

            case 'buttons':
              return (
                <Box key={key} sx={{ marginBottom: 2, marginTop: 3 }}>
                  <label>
                    {field.label}
                    {/* 	{field.required && ' *'} */}
                  </label>

                  <div className="flex flex-row">
                    <div>
                      <ButtonGroup
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          marginTop: '8px',
                          marginRight: '8px',
                          textTransform: 'none',
                        }}
                        variant="outlined">
                        {field.options.map((option) => {
                          // Проверяем выделен ли вариант:
                          // - если multiple=true, то fieldValues[key] – массив ID,
                          // - если multiple=false, то fieldValues[key] – одиночный ID
                          const isSelected = field.multiple
                            ? (fieldValues[key] || []).includes(option.id)
                            : fieldValues[key] === option.id;

                          return (
                            <Button
                              key={option.id}
                              variant={isSelected ? 'contained' : 'outlined'}
                              sx={{ textTransform: 'none' }}
                              onClick={() => handleToggle(key, option.id, field.multiple)}>
                              {option.value}
                            </Button>
                          );
                        })}
                      </ButtonGroup>
                      {errors[key] && <FormHelperText error>{errors[key]}</FormHelperText>}
                    </div>

                    {field.required && (
                      <div className="text-orange-500 font-extrabold text-xl">*</div>
                    )}
                  </div>
                </Box>
              );

            case 'checkbox':
              return (
                <Box key={key} sx={{ marginBottom: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!fieldValues[key]}
                        onChange={(e) => handleChange(key, e.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                </Box>
              );
            default:
              return null;
          }
        })}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '10vh',
        }}>
        {wasValidated && Object.keys(errors).length > 0 && (
          <Box
            sx={{
              padding: 2,
              marginBottom: 2,
              backgroundColor: '#FEE2E2',
              borderRadius: 1,
              color: '#B91C1C',
            }}>
            <Typography variant="body1" fontWeight="medium">
              Пожалуйста, заполните все обязательные поля, отмеченные звездочкой (*).
            </Typography>
          </Box>
        )}
      </div>
    </div>
  );
}

export default DynamicFields;
